import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { assignIntakeForm } from '../../../services/intakeFormService';
import { getClients } from '../../../services/clientService';
import Modal from '../../common/Modal';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const schema = z.object({
  clientId: z.string().regex(UUID, 'Select a client'),
});

export default function AssignFormModal({ formId, formTitle, isOpen, onClose }) {
  const queryClient = useQueryClient();

  const { data: clientsData } = useQuery({
    queryKey: ['clients', { limit: 200 }],
    queryFn: () => getClients({ limit: 200 }),
    enabled: isOpen,
  });
  const clients = clientsData?.clients ?? [];

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { clientId: '' },
  });

  const mutation = useMutation({
    mutationFn: ({ clientId }) => assignIntakeForm(formId, clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intakeForm', formId] });
      toast.success('Form assigned to client');
      reset();
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to assign form'),
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Assign "${formTitle}"`} size="sm">
      <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Client *</label>
          <select className="input" {...register('clientId')}>
            <option value="">Select client…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.lastName}, {c.firstName}</option>
            ))}
          </select>
          {errors.clientId && <p className="mt-1 text-sm text-danger-600">{errors.clientId.message}</p>}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button type="submit" disabled={mutation.isPending} className="btn btn-primary disabled:opacity-60">
            {mutation.isPending ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
